/**
 * DiffTreeShared — single source of truth for tree-diff rendering primitives.
 *
 * Used by both CommitDialog (3-column staging) and TwoColumnDiffTree (read-only compare).
 * All badge config, node styling, ghost injection, and detail-view logic lives here.
 */
import { useState, useMemo } from "react";
import {
  ChevronDown, ChevronRight, ChevronLeft, FolderOpen, FileText,
  ArrowLeft, ArrowUp, ArrowDown,
} from "lucide-react";
import RenderedDiffView from "./RenderedDiffView";

/* ════════════════════════════════════════════════════════════════════
   Badge config + StatusBadge
   ════════════════════════════════════════════════════════════════════ */

export const BADGE: Record<string, { label: string; bg: string; text: string; dot: string }> = {
  added:     { label: "ADD", bg: "bg-emerald-50", text: "text-emerald-600", dot: "bg-emerald-400" },
  deleted:   { label: "DEL", bg: "bg-red-50",     text: "text-red-500",     dot: "bg-red-400" },
  modified:  { label: "MOD", bg: "bg-blue-50",    text: "text-blue-600",    dot: "bg-blue-400" },
  moved:     { label: "MOV", bg: "bg-violet-50",  text: "text-violet-600",  dot: "bg-violet-400" },
  renamed:   { label: "REN", bg: "bg-fuchsia-50", text: "text-fuchsia-600", dot: "bg-fuchsia-400" },
  reordered: { label: "ORD", bg: "bg-amber-50",   text: "text-amber-600",   dot: "bg-amber-400" },
};

export function StatusBadge({ label, color }: { label: string; color: string }) {
  const colors: Record<string, string> = {
    violet: "bg-violet-50 text-violet-500 ring-violet-200",
    fuchsia: "bg-fuchsia-50 text-fuchsia-500 ring-fuchsia-200",
    red: "bg-red-50 text-red-500 ring-red-200",
    emerald: "bg-emerald-50 text-emerald-600 ring-emerald-200",
    blue: "bg-blue-50 text-blue-500 ring-blue-200",
    amber: "bg-amber-50 text-amber-600 ring-amber-200",
  };
  return (
    <span className={`text-[8px] leading-none px-1 py-[2px] rounded font-semibold uppercase tracking-wide shrink-0 select-none ring-1 ring-inset ${colors[color] || ""}`}>
      {label}
    </span>
  );
}

export function badgeColorFor(status: string): string {
  if (status === "moved") return "violet";
  if (status === "renamed") return "fuchsia";
  if (status === "deleted") return "red";
  if (status === "added") return "emerald";
  if (status === "modified") return "blue";
  if (status === "reordered") return "amber";
  return "blue";
}


/* ════════════════════════════════════════════════════════════════════
   Styling helpers
   ════════════════════════════════════════════════════════════════════ */

/** Solid left-border accent + tinted bg for a changed node row. */
export function actionStyle(sts: string[]): string | undefined {
  if (sts.includes("deleted")) return "border-l-2 border-l-red-400 bg-red-50";
  if (sts.includes("added")) return "border-l-2 border-l-emerald-400 bg-emerald-50";
  if (sts.includes("moved")) return "border-l-2 border-l-violet-400 bg-violet-50";
  if (sts.includes("modified")) return "border-l-2 border-l-blue-400 bg-blue-50/60";
  if (sts.includes("reordered")) return "border-l-2 border-l-amber-400 bg-amber-50";
  return undefined;
}

/** Lighter bg for children inside a structural change. */
export function actionBg(sts: string[]): string | undefined {
  if (sts.includes("added")) return "bg-emerald-50/60";
  if (sts.includes("deleted")) return "bg-red-50/60";
  if (sts.includes("moved")) return "bg-violet-50/60";
  if (sts.includes("reordered")) return "bg-amber-50/60";
  return undefined;
}

/* ════════════════════════════════════════════════════════════════════
   PanelNode — shared tree node shape
   ════════════════════════════════════════════════════════════════════ */

export interface PanelNode {
  slug: string;
  title: string;
  slugPath: string;
  nodeType: string;
  statuses: string[];
  change?: any;          // StagedChange or ChangeEntry — kept generic
  children: PanelNode[];
  isDeletedGhost?: boolean;
  isAddedGhost?: boolean;
  /** Invisible alignment placeholder — keeps rows in sync across columns */
  isSyncGhost?: boolean;
}

/* ════════════════════════════════════════════════════════════════════
   SyncScrollIcon
   ════════════════════════════════════════════════════════════════════ */

export function SyncScrollIcon({ synced, className }: { synced: boolean; className?: string }) {
  return (
    <svg viewBox="0 0 24 18" fill="none" className={className} stroke="currentColor" strokeLinecap="round">
      <rect x="1" y="1" width="9.5" height="16" rx="1.5" strokeWidth="1.4" />
      <line x1="3.5" y1="5" x2="8" y2="5" strokeWidth="1.6" opacity="0.7" />
      <line x1="3.5" y1="9" x2="8" y2="9" strokeWidth="1.6" opacity="0.7" />
      <line x1="3.5" y1="13" x2="6.5" y2="13" strokeWidth="1.6" opacity="0.7" />
      <rect x="13.5" y="1" width="9.5" height="16" rx="1.5" strokeWidth="1.4" />
      {synced ? (<>
        <line x1="16" y1="5" x2="20.5" y2="5" strokeWidth="1.6" opacity="0.7" />
        <line x1="16" y1="9" x2="20.5" y2="9" strokeWidth="1.6" opacity="0.7" />
        <line x1="16" y1="13" x2="19" y2="13" strokeWidth="1.6" opacity="0.7" />
      </>) : (<>
        <line x1="16" y1="4" x2="20.5" y2="4" strokeWidth="1.6" opacity="0.7" />
        <line x1="16" y1="8" x2="20.5" y2="8" strokeWidth="1.6" opacity="0.7" />
        <line x1="16" y1="12" x2="19" y2="12" strokeWidth="1.6" opacity="0.7" />
      </>)}
    </svg>
  );
}


/* ════════════════════════════════════════════════════════════════════
   DiffTreePanelNode — the core tree row renderer
   
   This is the single source of truth for tree node rendering.
   CommitDialog wraps this with staging action buttons via the
   `trailing` render prop. TwoColumnDiffTree uses it directly.
   ════════════════════════════════════════════════════════════════════ */

export function subtreeHasChanges(n: PanelNode): boolean {
  if (n.statuses.length > 0) return true;
  return n.children.some(subtreeHasChanges);
}

export interface DiffTreePanelNodeProps {
  node: PanelNode;
  depth: number;
  expandMap: Record<string, boolean>;
  onToggle: (sp: string) => void;
  selectedSlug: string | null;
  onNodeSelect: (sp: string) => void;
  /** Optional: only show badges when this returns true for the node's change */
  showBadge?: (node: PanelNode) => boolean;
  /** Optional: parent bg color to propagate into children for structural changes */
  ownerBg?: string;
  insideOwner?: boolean;
  /** Optional: render extra content (action buttons) in the trailing area */
  renderTrailingExtra?: (node: PanelNode) => React.ReactNode;
  /** Optional: compute a change count for a node (e.g. block-level diff count) */
  changeCountFn?: (node: PanelNode) => number | null;

  /** Optional: conflict paths to highlight */
  conflictPaths?: Set<string>;
  /** Optional: render node as ghost/muted when this returns true (used by staged panel for unstaged items) */
  muteUnstaged?: (node: PanelNode) => boolean;
}

export function DiffTreePanelNode({
  node, depth, expandMap, onToggle, selectedSlug, onNodeSelect,
  showBadge: showBadgeFn, ownerBg, insideOwner,
  renderTrailingExtra, changeCountFn, conflictPaths, muteUnstaged,
}: DiffTreePanelNodeProps) {
  const { slugPath, statuses, children } = node;
  const hasChildren = children.length > 0;
  const expanded = expandMap[slugPath] ?? false;

  // Sync ghost: muted grey placeholder row to keep columns aligned
  // Tinted subtly when the ghost represents an added or deleted node
  if (node.isSyncGhost) {
    const isAddTinted = node.isAddedGhost;
    const isDelTinted = node.isDeletedGhost;
    const isMovTinted = !isAddTinted && !isDelTinted && (node.statuses?.includes("moved") || node.statuses?.includes("renamed"));
    const ghostBg = isDelTinted ? "bg-red-50/30" : isAddTinted ? "bg-emerald-50/30" : isMovTinted ? "bg-blue-50/30" : "bg-slate-100/60";
    const ghostIconColor = isDelTinted ? "text-red-200" : isAddTinted ? "text-emerald-200" : isMovTinted ? "text-blue-200" : "text-slate-300";
    const ghostTextColor = isDelTinted ? "text-red-300 italic" : isAddTinted ? "text-emerald-300 italic" : isMovTinted ? "text-blue-300 italic" : "text-slate-400 italic";
    return (
      <div>
        <div
          className={`flex items-center gap-1.5 px-2 h-[32px] ${ghostBg} rounded`}
          data-slugpath={slugPath}
          style={{ paddingLeft: depth * 14 }}
        >
          {hasChildren ? (
            <button onClick={e => { e.stopPropagation(); onToggle(slugPath); }} className="shrink-0 p-0">
              {expanded ? <ChevronDown className={`size-3.5 ${ghostIconColor}`} /> : <ChevronRight className={`size-3.5 ${ghostIconColor}`} />}
            </button>
          ) : <span className="w-3 shrink-0" />}
          {hasChildren ? <FolderOpen className={`size-3.5 ${ghostIconColor} shrink-0`} /> : <FileText className={`size-3.5 ${ghostIconColor} shrink-0`} />}
          <span className={`truncate ${ghostTextColor} text-[13px]`}>{node.title}</span>
        </div>
        {expanded && children.map(c => (
          <DiffTreePanelNode
            key={c.slugPath} node={c} depth={depth + 1}
            expandMap={expandMap} onToggle={onToggle}
            selectedSlug={selectedSlug} onNodeSelect={onNodeSelect}
            showBadge={showBadgeFn} ownerBg={undefined} insideOwner={false}
            renderTrailingExtra={renderTrailingExtra}
            changeCountFn={changeCountFn}
            conflictPaths={conflictPaths}
            muteUnstaged={muteUnstaged}
          />
        ))}
      </div>
    );
  }

  const isSelected = selectedSlug === slugPath;
  const isConflict = conflictPaths?.has(slugPath) ?? false;

  const shouldShowBadge = showBadgeFn ? showBadgeFn(node) : statuses.length > 0;

  // Check if collapsed subtree has changes (for amber dot indicator)
  const hasChangesBelow = subtreeHasChanges(node);
  const collapsedWithChanges = hasChildren && !expanded && hasChangesBelow;

  // Compute badge elements
  const changeCount = changeCountFn ? changeCountFn(node) : null;
  const badges = shouldShowBadge && statuses.length > 0 ? statuses.map(s => {
    const cfg = BADGE[s]; if (!cfg) return null;
    const color = badgeColorFor(s);
    const countLabel = (s === "modified" && changeCount && changeCount > 0) ? `${cfg.label} ${changeCount}` : cfg.label;
    return <StatusBadge key={s} label={countLabel} color={color} />;
  }).filter(Boolean) : null;

  // Determine highlight: solid left-border accent + tinted bg
  const isOwner = shouldShowBadge && statuses.length > 0;
  const ownStyle = isOwner ? actionStyle(statuses) : undefined;
  const ownBg = isOwner ? actionBg(statuses) : undefined;
  const bg = ownStyle || (insideOwner ? ownerBg : undefined);

  // Propagate bg for structural actions (added/deleted/moved/reordered).
  // "modified" is content-only — don't bleed to children.
  const isStructural = statuses.some(s => s === "added" || s === "deleted" || s === "moved" || s === "reordered");
  const childOwnerBg = (isOwner && isStructural ? ownBg : undefined) || ownerBg;
  const childInsideOwner = !!((isOwner && isStructural) || insideOwner);

  // Trailing area: badges + optional extra (action buttons from parent)
  const trailingExtra = renderTrailingExtra ? renderTrailingExtra(node) : null;
  const hasTrailing = badges || trailingExtra;
  const trailing = hasTrailing ? (
    <span className="flex items-center gap-1 ml-auto shrink-0">
      {badges && <span className="flex items-center gap-0.5 shrink-0">{badges}</span>}
      {trailingExtra && <span className="shrink-0">{trailingExtra}</span>}
    </span>
  ) : null;

  const visualDeletedGhost = node.isDeletedGhost;
  const visualAddedGhost = node.isAddedGhost;
  const isMuted = muteUnstaged ? muteUnstaged(node) : false;

  const ghostClass = visualDeletedGhost
    ? "border border-dashed border-red-100 bg-red-50/20 rounded opacity-60"
    : visualAddedGhost
    ? "border border-dashed border-emerald-100 bg-emerald-50/20 rounded opacity-60"
    : isMuted
    ? statuses.includes("added")
      ? "border border-dashed border-emerald-200 bg-emerald-50/30 rounded"
      : statuses.includes("deleted")
      ? "border border-dashed border-red-100 bg-red-50/20 rounded opacity-60"
      : "border border-dashed border-slate-200 bg-slate-50/30 rounded"
    : "";

  return (
    <div>
      <div
        className={`flex items-center gap-1.5 px-2 text-[13px] text-slate-900 cursor-pointer h-[32px] ${ghostClass} ${!visualDeletedGhost && !visualAddedGhost && !isMuted && bg ? bg : ""} ${isSelected ? `ring-1 ring-inset ring-indigo-400 ${!bg && !ghostClass ? "bg-indigo-50/30" : ""}` : ""} ${!ghostClass && collapsedWithChanges && !bg?.includes("border-l") ? "border-l-2 border-l-amber-400" : ""} ${isConflict ? "border-l-2 border-l-red-400 bg-red-50/30" : ""}`}
        data-slugpath={slugPath}
        style={{ paddingLeft: depth * 14 }}
        onClick={() => onNodeSelect(slugPath)}
      >
        {visualDeletedGhost ? (
          <>
            {hasChildren ? (
              <button onClick={e => { e.stopPropagation(); onToggle(slugPath); }} className="shrink-0 p-0">
                {expanded ? <ChevronDown className="size-3.5 text-red-200" /> : <ChevronRight className="size-3.5 text-red-200" />}
              </button>
            ) : <span className="w-3 shrink-0" />}
            {hasChildren ? <FolderOpen className="size-3.5 text-red-200 shrink-0" /> : <FileText className="size-3.5 text-red-200 shrink-0" />}
            <span className="truncate text-red-300 italic">{node.title}</span>
            {trailing}
          </>
        ) : visualAddedGhost ? (
          <>
            {hasChildren ? (
              <button onClick={e => { e.stopPropagation(); onToggle(slugPath); }} className="shrink-0 p-0">
                {expanded ? <ChevronDown className="size-3.5 text-emerald-200" /> : <ChevronRight className="size-3.5 text-emerald-200" />}
              </button>
            ) : <span className="w-3 shrink-0" />}
            {hasChildren ? <FolderOpen className="size-3.5 text-emerald-200 shrink-0" /> : <FileText className="size-3.5 text-emerald-200 shrink-0" />}
            <span className="truncate text-emerald-300 italic">{node.title}</span>
            {trailing}
          </>
        ) : isMuted ? (
          <>
            {hasChildren ? (
              <button onClick={e => { e.stopPropagation(); onToggle(slugPath); }} className="shrink-0 p-0">
                {expanded
                  ? <ChevronDown className={`size-3.5 ${statuses.includes("added") ? "text-emerald-300" : statuses.includes("deleted") ? "text-red-300" : "text-slate-300"}`} />
                  : <ChevronRight className={`size-3.5 ${statuses.includes("added") ? "text-emerald-300" : statuses.includes("deleted") ? "text-red-300" : "text-slate-300"}`} />}
              </button>
            ) : <span className="w-3 shrink-0" />}
            {hasChildren
              ? <FolderOpen className={`size-3.5 shrink-0 ${statuses.includes("added") ? "text-emerald-300" : statuses.includes("deleted") ? "text-red-300" : "text-slate-300"}`} />
              : <FileText className={`size-3.5 shrink-0 ${statuses.includes("added") ? "text-emerald-300" : statuses.includes("deleted") ? "text-red-300" : "text-slate-300"}`} />}
            <span className={`truncate italic ${statuses.includes("added") ? "text-emerald-400" : statuses.includes("deleted") ? "text-red-400" : "text-slate-400"}`}>{node.title}</span>
            {trailing}
          </>
        ) : (
          <>
            {hasChildren ? (
              <button onClick={e => { e.stopPropagation(); onToggle(slugPath); }} className="shrink-0 p-0">
                {expanded ? <ChevronDown className="size-3.5 text-slate-900" /> : <ChevronRight className="size-3.5 text-slate-900" />}
              </button>
            ) : <span className="w-3 shrink-0" />}
            {hasChildren ? <FolderOpen className="size-3.5 text-amber-500 shrink-0" /> : <FileText className="size-3.5 text-slate-900 shrink-0" />}
            <span className="truncate">{node.title}</span>
            {collapsedWithChanges && !badges && <span className="size-1.5 rounded-full bg-amber-400 shrink-0" title="Changes inside" />}
            {trailing}
          </>
        )}
      </div>
      {expanded && children.map(c => (
        <DiffTreePanelNode
          key={c.slugPath} node={c} depth={depth + 1}
          expandMap={expandMap} onToggle={onToggle}
          selectedSlug={selectedSlug} onNodeSelect={onNodeSelect}
          showBadge={showBadgeFn} ownerBg={childOwnerBg} insideOwner={childInsideOwner}
          renderTrailingExtra={renderTrailingExtra}
          changeCountFn={changeCountFn}
          conflictPaths={conflictPaths}
          muteUnstaged={muteUnstaged}
        />
      ))}
    </div>
  );
}


/* ════════════════════════════════════════════════════════════════════
   Ghost injection utilities
   ════════════════════════════════════════════════════════════════════ */

/** Recursively clone a source subtree as deleted ghost nodes. */
export function cloneAsDeletedGhost(sn: PanelNode, changeMap: Map<string, any>, lookup?: (sp: string) => any): PanelNode {
  const lk = lookup || ((sp: string) => changeMap.get(sp));
  const ch = lk(sn.slugPath);
  return {
    slug: sn.slug, title: sn.title, slugPath: sn.slugPath,
    nodeType: sn.nodeType,
    statuses: ch ? [...(ch.actions || ch.changeTypes || ["deleted"])] : ["deleted"],
    change: ch,
    children: sn.children.map(c => cloneAsDeletedGhost(c, changeMap, lookup)),
    isDeletedGhost: true,
  };
}

/** Recursively clone a draft subtree as added ghost nodes. */
export function cloneAsAddedGhost(dn: PanelNode, changeMap: Map<string, any>): PanelNode {
  const ch = changeMap.get(dn.slugPath);
  return {
    slug: dn.slug, title: dn.title, slugPath: dn.slugPath,
    nodeType: dn.nodeType,
    statuses: ch ? [...(ch.actions || ch.changeTypes || ["added"])] : ["added"],
    change: ch,
    children: dn.children.map(c => cloneAsAddedGhost(c, changeMap)),
    isAddedGhost: true,
  };
}

/** Inject deleted-node ghost placeholders into a target tree.
 *  Walks the source tree and for any node with a "deleted" change,
 *  inserts a ghost PanelNode into the target tree at the matching position. */
export function injectDeletedGhosts(
  target: PanelNode[], srcNodes: PanelNode[],
  changeMap: Map<string, any>,
  lookupChange?: (slugPath: string) => any,
): PanelNode[] {
  const lookup = lookupChange || ((sp: string) => changeMap.get(sp));
  const targetBySlug = new Map(target.map(n => [n.slug, n]));

  const ghosts = new Map<string, PanelNode>();
  for (const sn of srcNodes) {
    const ch = lookup(sn.slugPath);
    const actions: string[] = ch?.actions || ch?.changeTypes || [];
    if (actions.includes("deleted") && !targetBySlug.has(sn.slug)) {
      ghosts.set(sn.slug, cloneAsDeletedGhost(sn, changeMap, lookup));
    }
  }

  if (ghosts.size === 0) {
    for (const sn of srcNodes) {
      const ch = lookup(sn.slugPath);
      const actions: string[] = ch?.actions || ch?.changeTypes || [];
      if (!actions.includes("deleted")) {
        const tNode = targetBySlug.get(sn.slug);
        if (tNode) tNode.children = injectDeletedGhosts(tNode.children, sn.children, changeMap, lookupChange);
      }
    }
    return target;
  }

  const result: PanelNode[] = [];
  const placed = new Set<string>();
  for (const sn of srcNodes) {
    const tNode = targetBySlug.get(sn.slug);
    const ghost = ghosts.get(sn.slug);
    if (ghost) { result.push(ghost); placed.add(sn.slug); }
    else if (tNode) {
      const ch = lookup(sn.slugPath);
      const actions: string[] = ch?.actions || ch?.changeTypes || [];
      if (!actions.includes("deleted")) {
        tNode.children = injectDeletedGhosts(tNode.children, sn.children, changeMap, lookupChange);
      }
      result.push(tNode); placed.add(sn.slug);
    }
  }
  for (const tn of target) { if (!placed.has(tn.slug)) result.push(tn); }
  return result;
}

/** Inject added-node ghost placeholders into the source tree. */
export function injectAddedGhosts(
  srcNodes: PanelNode[], dftNodes: PanelNode[],
  changeMap: Map<string, any>,
): PanelNode[] {
  const srcBySlug = new Map(srcNodes.map(n => [n.slug, n]));

  const ghosts = new Map<string, PanelNode>();
  for (const dn of dftNodes) {
    const ch = changeMap.get(dn.slugPath);
    const actions: string[] = ch?.actions || ch?.changeTypes || [];
    if (actions.includes("added") && !srcBySlug.has(dn.slug)) {
      ghosts.set(dn.slug, cloneAsAddedGhost(dn, changeMap));
    }
  }

  if (ghosts.size === 0) {
    for (const dn of dftNodes) {
      const ch = changeMap.get(dn.slugPath);
      const actions: string[] = ch?.actions || ch?.changeTypes || [];
      if (!actions.includes("added")) {
        const sNode = srcBySlug.get(dn.slug);
        if (sNode) sNode.children = injectAddedGhosts(sNode.children, dn.children, changeMap);
      }
    }
    return srcNodes;
  }

  const result: PanelNode[] = [];
  const placed = new Set<string>();
  for (const dn of dftNodes) {
    const ghost = ghosts.get(dn.slug);
    const sNode = srcBySlug.get(dn.slug);
    if (ghost) { result.push(ghost); placed.add(dn.slug); }
    else if (sNode) {
      const ch = changeMap.get(dn.slugPath);
      const actions: string[] = ch?.actions || ch?.changeTypes || [];
      if (!actions.includes("added")) {
        sNode.children = injectAddedGhosts(sNode.children, dn.children, changeMap);
      }
      result.push(sNode); placed.add(dn.slug);
    }
  }
  for (const sn of srcNodes) { if (!placed.has(sn.slug)) result.push(sn); }
  return result;
}


/* ════════════════════════════════════════════════════════════════════
   syncTrees — ensure multiple trees have the same set of nodes at
   every level. Missing nodes get `isSyncGhost` placeholders so rows
   stay aligned across columns.
   ════════════════════════════════════════════════════════════════════ */

export function syncTrees(...trees: PanelNode[][]): void {
  const seen = new Map<string, PanelNode>();
  const order: string[] = [];
  for (const tree of trees) {
    for (const n of tree) {
      if (!seen.has(n.slugPath)) {
        seen.set(n.slugPath, n);
        order.push(n.slugPath);
      }
    }
  }

  for (let ti = 0; ti < trees.length; ti++) {
    const existing = new Map(trees[ti].map(n => [n.slugPath, n]));
    const existingSlugs = new Set(trees[ti].map(n => n.slug));
    const synced: PanelNode[] = [];
    for (const sp of order) {
      const node = existing.get(sp);
      if (node) {
        synced.push(node);
      } else {
        const real = seen.get(sp)!;
        if (existingSlugs.has(real.slug)) continue;
        synced.push(makeSyncGhost(real));
        existingSlugs.add(real.slug);
      }
    }
    trees[ti].length = 0;
    trees[ti].push(...synced);
  }

  for (const sp of order) {
    const childArrays = trees.map(tree => {
      const node = tree.find(n => n.slugPath === sp);
      return node ? node.children : [];
    });
    if (childArrays.some(arr => arr.length > 0)) {
      // Check if this parent has an ORD (reordered) status — if so, each column
      // keeps its own native child ordering. We only ensure the same set of
      // children exists (no reordering, no ghosts needed for pure reorder).
      const parentNode = seen.get(sp);
      const isReordered = parentNode?.statuses?.includes("reordered");
      if (isReordered) {
        syncTreesPreserveOrder(...childArrays);
      } else {
        syncTrees(...childArrays);
      }
    }
  }
}

/**
 * Like syncTrees but preserves each tree's native ordering.
 * Only ensures the same set of children exists — missing nodes get ghosts
 * appended at the end rather than inserted at a merged position.
 * Used for children of ORD parents so the reorder difference is visible.
 */
function syncTreesPreserveOrder(...trees: PanelNode[][]): void {
  // Collect all unique nodes across all trees
  const seen = new Map<string, PanelNode>();
  for (const tree of trees) {
    for (const n of tree) {
      if (!seen.has(n.slugPath)) seen.set(n.slugPath, n);
    }
  }

  // For each tree, keep its existing nodes in their native order,
  // then append ghosts for any missing nodes
  for (let ti = 0; ti < trees.length; ti++) {
    const existingSlugs = new Set(trees[ti].map(n => n.slug));
    const existingPaths = new Set(trees[ti].map(n => n.slugPath));
    for (const [sp, real] of seen) {
      if (!existingPaths.has(sp) && !existingSlugs.has(real.slug)) {
        trees[ti].push(makeSyncGhost(real));
        existingSlugs.add(real.slug);
      }
    }
  }

  // Recurse into children — continue preserving order for nested levels
  const allPaths = new Set<string>();
  for (const tree of trees) for (const n of tree) allPaths.add(n.slugPath);

  for (const sp of allPaths) {
    const childArrays = trees.map(tree => {
      const node = tree.find(n => n.slugPath === sp);
      return node ? node.children : [];
    });
    if (childArrays.some(arr => arr.length > 0)) {
      syncTreesPreserveOrder(...childArrays);
    }
  }
}

export function makeSyncGhost(real: PanelNode, inheritTint?: { isAddedGhost?: boolean; isDeletedGhost?: boolean; moveTinted?: boolean }): PanelNode {
  const isAdd = inheritTint?.isAddedGhost || real.isAddedGhost || real.statuses?.includes("added") || undefined;
  const isDel = inheritTint?.isDeletedGhost || real.isDeletedGhost || real.statuses?.includes("deleted") || undefined;
  const isMov = inheritTint?.moveTinted || (!isAdd && !isDel && (real.statuses?.includes("moved") || real.statuses?.includes("renamed"))) || undefined;
  // Propagate tint to children so entire subtree gets the color
  const childTint = (isAdd || isDel || isMov) ? { isAddedGhost: isAdd, isDeletedGhost: isDel, moveTinted: isMov } : inheritTint;
  return {
    slug: real.slug, title: real.title, slugPath: real.slugPath,
    nodeType: real.nodeType,
    statuses: isMov ? [...(real.statuses || []), ...(real.statuses?.includes("moved") ? [] : ["moved"])] : (real.statuses || []),
    children: real.children.map(c => makeSyncGhost(c, childTint)),
    isSyncGhost: true,
    isAddedGhost: isAdd,
    isDeletedGhost: isDel,
  };
}


/* ════════════════════════════════════════════════════════════════════
   DiffDetailView — full-width side-by-side diff viewer
   
   Generic version used by both CommitDialog and TwoColumnDiffTree.
   Accepts a uniform shape for the change data.
   ════════════════════════════════════════════════════════════════════ */

export interface DiffDetailData {
  slugPath: string;
  title: string;
  actions: string[];
  changeTypes?: string[];
  beforeBody: string;
  afterBody: string;
  beforeTitle?: string;
  afterTitle?: string;
  originalSlugPath?: string;
  nodeType?: string;
  changedAssets?: any;
  beforeSpec?: Record<string, any> | null;
  afterSpec?: Record<string, any> | null;
  // Support / FAQ typed metadata. SectionType lets the renderer
  // decide whether to show the SupportMetaDiffView panel.
  beforeMeta?: Record<string, any> | null;
  afterMeta?: Record<string, any> | null;
  sectionType?: string | null;
}

export interface DiffDetailViewProps {
  detail: DiffDetailData;
  onBack: () => void;
  /** All previewable changes for prev/next navigation */
  allChanges: DiffDetailData[];
  onNavigate?: (c: DiffDetailData) => void;
  loading?: boolean;
  nodeType?: string;
  /** Labels for the two diff panes */
  beforeLabel?: string;
  afterLabel?: string;
  /** Optional extra header content (e.g. branch badge, close button) */
  headerExtra?: React.ReactNode;
}

export function DiffDetailView({
  detail, onBack, allChanges, onNavigate, loading, nodeType,
  beforeLabel = "Before", afterLabel = "After", headerExtra,
}: DiffDetailViewProps) {
  const actions = detail.actions;
  const isReorder = actions.includes("reordered");
  const hasDelete = actions.includes("deleted");
  const resolvedNodeType = nodeType || detail.nodeType;
  const isApiEndpoint = resolvedNodeType === "api_endpoint";

  const [mode, setMode] = useState<"rendered" | "source">("rendered");
  const [syncScroll, setSyncScroll] = useState(true);

  const previewable = useMemo(() => allChanges.filter(c => {
    const a = c.actions;
    return a.includes("modified") || a.includes("added") || a.includes("deleted") || a.includes("reordered");
  }), [allChanges]);
  const currentIdx = previewable.findIndex(c => c.slugPath === detail.slugPath);
  const hasPrev = currentIdx > 0;
  const hasNext = currentIdx >= 0 && currentIdx < previewable.length - 1;

  // Reorder data
  let reorderOld: { order: string[]; titles: Record<string, string> } | null = null;
  let reorderNew: { order: string[]; titles: Record<string, string> } | null = null;
  if (isReorder) {
    try { reorderOld = JSON.parse(detail.beforeBody); } catch { /* ignore */ }
    try { reorderNew = JSON.parse(detail.afterBody); } catch { /* ignore */ }
  }
  const titles = reorderNew?.titles || reorderOld?.titles || {};
  const label = (slug: string) => titles[slug] || slug;

  return (
    <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
      {/* Header bar */}
      <div className="flex items-center gap-3 px-4 py-2.5 border-b border-slate-200 bg-slate-50/80 shrink-0">
        <button onClick={onBack}
          className="shrink-0 flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-[13px] font-medium text-slate-700 bg-white border border-slate-200 hover:border-indigo-400 hover:text-indigo-600 shadow-sm transition-colors">
          <ArrowLeft className="size-4" /> Back
        </button>

        <div className="flex items-center gap-2 min-w-0 flex-1">
          {actions.map(a => {
            const b = BADGE[a]; if (!b) return null;
            return <span key={a} className={`shrink-0 text-[11px] px-2 py-0.5 rounded font-semibold ${b.bg} ${b.text}`}>{b.label}</span>;
          })}
          <FileText className="size-4 text-slate-400 shrink-0" />
          <span className="text-[14px] font-semibold text-slate-800 truncate">
            {detail.slugPath === "__root__" ? "Content" : detail.title}
          </span>
          {!isReorder && <span className="text-[12px] font-mono text-slate-400 truncate">{detail.slugPath}</span>}
        </div>

        {/* Prev / Next */}
        {previewable.length > 0 && onNavigate && (
          <div className="flex items-center rounded-md border border-slate-200 bg-white shadow-sm overflow-hidden shrink-0">
            <button onClick={() => hasPrev && onNavigate(previewable[currentIdx - 1])} disabled={!hasPrev}
              className="px-2 py-1.5 text-slate-500 hover:text-indigo-600 hover:bg-indigo-50 disabled:text-slate-200 disabled:cursor-not-allowed transition-colors border-r border-slate-200" title="Previous change">
              <ChevronLeft className="size-4" />
            </button>
            <span className="px-3 py-1.5 text-[12px] text-slate-600 font-semibold tabular-nums select-none bg-slate-50/50">
              {currentIdx >= 0 ? `${currentIdx + 1} / ${previewable.length}` : "–"}
            </span>
            <button onClick={() => hasNext && onNavigate(previewable[currentIdx + 1])} disabled={!hasNext}
              className="px-2 py-1.5 text-slate-500 hover:text-indigo-600 hover:bg-indigo-50 disabled:text-slate-200 disabled:cursor-not-allowed transition-colors border-l border-slate-200" title="Next change">
              <ChevronRight className="size-4" />
            </button>
          </div>
        )}

        {/* Mode switcher */}
        {!isReorder && (
          <div className="flex rounded-md bg-slate-100 p-0.5 text-[12px] shrink-0">
            <button onClick={() => setMode("rendered")}
              className={`px-3 py-1.5 font-medium rounded-md transition-colors ${mode === "rendered" ? "bg-indigo-50 text-indigo-700 border border-indigo-200 shadow-sm" : "text-slate-500 hover:text-indigo-600 hover:bg-indigo-50/50"}`}>
              Rendered
            </button>
            <button onClick={() => setMode("source")}
              className={`px-3 py-1.5 font-medium rounded-md transition-colors ${mode === "source" ? "bg-indigo-50 text-indigo-700 border border-indigo-200 shadow-sm" : "text-slate-500 hover:text-indigo-600 hover:bg-indigo-50/50"}`}>
              Source
            </button>
          </div>
        )}

        {/* Sync scroll */}
        {!isReorder && (
          <button onClick={() => setSyncScroll(s => !s)} title={syncScroll ? "Unlock pane scrolling" : "Lock pane scrolling"}
            className={`p-1.5 rounded-md border shadow-sm transition-colors shrink-0 ${syncScroll ? "text-indigo-600 bg-indigo-50 border-indigo-200" : "text-slate-400 bg-white border-slate-200 hover:border-slate-300"}`}>
            <SyncScrollIcon synced={syncScroll} className="w-[22px] h-[16px]" />
          </button>
        )}

        {headerExtra}
      </div>

      {/* Title / slug change bar */}
      {!isReorder && (detail.beforeTitle !== detail.afterTitle || detail.originalSlugPath) && (
        <div className="flex items-center gap-3 px-5 py-1.5 border-b border-slate-100 bg-slate-50/30 shrink-0 text-[12px]">
          {detail.originalSlugPath && detail.originalSlugPath !== detail.slugPath && (
            <span className="flex items-center gap-1 text-slate-500">
              <span className="font-medium text-slate-600">Slug:</span>
              <span className="font-mono line-through text-red-400">{detail.originalSlugPath.split("/").pop()}</span>
              <span className="text-slate-400">→</span>
              <span className="font-mono text-emerald-600">{detail.slugPath.split("/").pop()}</span>
            </span>
          )}
          {detail.beforeTitle !== detail.afterTitle && detail.beforeTitle && detail.afterTitle && (
            <span className="flex items-center gap-1 text-slate-500">
              <span className="font-medium text-slate-600">Title:</span>
              <span className="line-through text-red-400">{detail.beforeTitle}</span>
              <span className="text-slate-400">→</span>
              <span className="text-emerald-600">{detail.afterTitle}</span>
            </span>
          )}
        </div>
      )}

      {/* Reorder view */}
      {isReorder && reorderOld && reorderNew ? (
        <div className="flex-1 flex min-h-0 overflow-hidden">
          <div className="flex-1 min-w-0 flex flex-col border-r border-slate-200 min-h-0">
            <div className="px-4 py-2 bg-slate-50 border-b border-slate-200 shrink-0 flex items-center gap-1.5">
              <div className="size-1.5 rounded-full bg-slate-400" />
              <span className="text-[13px] font-semibold text-slate-600">{beforeLabel}</span>
            </div>
            <div className="flex-1 overflow-auto px-4 py-3 scrollbar-mac">
              <div className="space-y-1">
                {reorderOld.order.map((slug, i) => {
                  const newIdx = reorderNew!.order.indexOf(slug);
                  const moved = newIdx !== i;
                  return (
                    <div key={slug} className={`flex items-center gap-2.5 py-1.5 px-3 rounded-lg border ${moved ? "border-amber-200 bg-amber-50/60" : "border-slate-100 bg-white"}`}>
                      <span className="text-[13px] text-slate-400 w-4 text-right tabular-nums font-mono">{i + 1}</span>
                      <span className={`text-[14px] ${moved ? "text-amber-700 font-medium" : "text-slate-700"}`}>{label(slug)}</span>
                      <span className="text-[11px] text-slate-400 font-mono ml-auto">{slug}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
          <div className="flex-1 min-w-0 flex flex-col min-h-0">
            <div className="px-4 py-2 bg-amber-50/50 border-b border-slate-200 shrink-0 flex items-center gap-1.5">
              <div className="size-1.5 rounded-full bg-orange-400" />
              <span className="text-[13px] font-semibold text-orange-700">{afterLabel}</span>
            </div>
            <div className="flex-1 overflow-auto px-4 py-3 scrollbar-mac">
              <div className="space-y-1">
                {reorderNew.order.map((slug, i) => {
                  const oldIdx = reorderOld!.order.indexOf(slug);
                  const delta = oldIdx - i;
                  return (
                    <div key={slug} className={`flex items-center gap-2.5 py-1.5 px-3 rounded-lg border ${delta !== 0 ? "border-amber-200 bg-amber-50/60" : "border-slate-100 bg-white"}`}>
                      <span className="text-[13px] text-slate-400 w-4 text-right tabular-nums font-mono">{i + 1}</span>
                      <span className={`text-[14px] ${delta !== 0 ? "text-amber-700 font-medium" : "text-slate-700"}`}>{label(slug)}</span>
                      {delta > 0 && <span className="flex items-center gap-0.5 text-[12px] text-emerald-600 ml-auto"><ArrowUp className="size-3.5" />{delta}</span>}
                      {delta < 0 && <span className="flex items-center gap-0.5 text-[12px] text-red-500 ml-auto"><ArrowDown className="size-3.5" />{Math.abs(delta)}</span>}
                      {delta === 0 && <span className="text-[11px] text-slate-400 font-mono ml-auto">{slug}</span>}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      ) : (
        /* Body diff + inline spec sections in one continuous scroll */
        <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
          <RenderedDiffView
            beforeBody={detail.beforeBody || ""}
            afterBody={hasDelete ? "" : (detail.afterBody || "")}
            beforeLabel={beforeLabel}
            afterLabel={hasDelete ? "Deleted" : afterLabel}
            changeActions={actions}
            changeTypes={detail.changeTypes}
            showHeader={false}
            loading={loading}
            slugPath={detail.slugPath}
            changedAssets={detail.changedAssets}
            nodeType={resolvedNodeType}
            beforeTitle={detail.beforeTitle}
            afterTitle={detail.afterTitle}
            externalMode={mode}
            onModeChange={setMode}
            externalSyncScroll={syncScroll}
            onSyncToggle={() => setSyncScroll(s => !s)}
            beforeSpec={isApiEndpoint ? (detail.beforeSpec ?? null) : undefined}
            afterSpec={isApiEndpoint ? (hasDelete ? null : (detail.afterSpec ?? null)) : undefined}
            beforeMeta={detail.beforeMeta ?? null}
            afterMeta={hasDelete ? null : (detail.afterMeta ?? null)}
            sectionType={detail.sectionType ?? null}
          />
        </div>
      )}
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════════
   TabBtn — pill-style tab button
   ════════════════════════════════════════════════════════════════════ */

export function TabBtn({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button onClick={onClick}
      className={`flex items-center px-3 py-1.5 text-[13px] font-medium rounded-md transition-colors ${
        active ? "bg-indigo-50 text-indigo-700 border border-indigo-200 shadow-sm" : "text-slate-500 hover:text-indigo-600 hover:bg-indigo-50/50"
      }`}>{children}</button>
  );
}
